import { UserRoles } from 'apps/orion/src/app/features/scm/material-management/v1/types';
import { Plant } from '../configuration';
import { isObject } from '../utils/object.utils';
import { LAST_LOGGED_IN_PRIMARY_SID_KEY } from './authentication.consts';

/**
 * Returns the code of the first plant marked as visible (`Show === true`).
 * @param {Plant[]} plants - List of plants to search. Each plant is expected
 * to have a boolean `Show` flag and a string `Code`.
 * @returns {string} The `Code` of the first visible plant.
 *
 * TODO: add type safe implementation after upgrade:
 * plants.find(p => p.Show)?.Code ?? '';
 */
const getDefaultPlant = (plants: Plant[]): string =>
  (Array.isArray(plants) ? plants : [])
    .filter((plant): boolean | undefined => plant.Show)
    .map((item): string => item.Code)[0] || '';

/**
 * Recursively intersects two rights objects. Used to combine role-derived rights with a plant
 * role's rights — the user keeps a permission only if BOTH the role and the plant grant it.
 *
 * @example
 * // SCM_Buyer role grants the user full portfolio access:
 * const role = {
 *   create: true, edit: true, delete: true,
 *   addition: { create: true, edit: true, delete: true },
 * };
 *
 * // JUSDA plant grants the user only create + addition.create:
 * const plant = {
 *   create: true,
 *   addition: { create: true },
 * };
 *
 * intersectRights(role, plant);
 * // → user can create portfolio + create addition; everything else denied:
 * // {
 * //   create: true, edit: false, delete: false,
 * //   addition: { create: true, edit: false, delete: false },
 * // }
 *
 * // If the plant grants no portfolio rights at all (e.g. CTP or JUSDA):
 * intersectRights(role, undefined);
 * // → user has no portfolio rights on that plant
 */
export const intersectRights = <T>(a?: T, b?: T): T => {
  const left = (a || {}) as Record<string, unknown>;
  const right = (b || {}) as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  new Set([...Object.keys(left), ...Object.keys(right)]).forEach((key): void => {
    const lv = left[key];
    const rv = right[key];
    out[key] = isObject(lv) || isObject(rv) ? intersectRights(lv, rv) : lv === true && rv === true;
  });

  return out as unknown as T;
};

/**
 * True if `code` matches a plant in `plants` that has `Show === true`.
 */
const isPlantAllowed = (code: string, plants: Plant[]): boolean =>
  !!code && Array.isArray(plants) && plants.some((p): boolean => p.Code === code && !!p.Show);

/**
 * Returns `candidate` when it identifies a `Show:true` plant in `plants`,
 * otherwise the default visible plant, otherwise empty string.
 *
 * Guards against stale `authentication.plant` carried over via NgxsStoragePlugin —
 * page refresh after BE revoked rights, or a shared computer where the previous
 * user's plant is still in localStorage. Without revalidation, requests would go
 * out with a plant the current user has no rights to and silently 403.
 */
export const resolveActivePlant = (
  candidate: null | Plant | string | undefined,
  plants: Plant[],
): string => {
  const list = Array.isArray(plants) ? plants : [];
  const code =
    typeof candidate === 'object' && candidate ? candidate.Code : (candidate as string) || '';
  return isPlantAllowed(code, list) ? code : getDefaultPlant(list);
};

/**
 * Type guard that checks whether a given value is a valid {@link UserRoles} member.
 *
 * @param role - The role value to check (may be a string, null, or undefined).
 * @returns `true` if the value is a valid `UserRoles` enum value, otherwise `false`.
 */
export const isUserRole = (role: null | string | undefined): role is UserRoles => {
  return Object.values(UserRoles).includes(role as UserRoles);
};

export const readLastLoggedInPrimarySid = (): null | string => {
  try {
    return localStorage.getItem(LAST_LOGGED_IN_PRIMARY_SID_KEY);
  } catch {
    return null;
  }
};

export const writeLastLoggedInPrimarySid = (primarySid: string | undefined): void => {
  if (!primarySid) return;
  try {
    localStorage.setItem(LAST_LOGGED_IN_PRIMARY_SID_KEY, primarySid);
  } catch {
    // error
  }
};
